// OCT-7340: TreatyApp: User Management — add an Action History view of admin activity - Frontend : ashish.atwal@resolutionlife.com
import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';

import { EmptyState } from '../../ui/errors/empty-state';
import { ErrorState } from '../../ui/errors/error-state';
import { pluralize } from '../../utils/pluralize/pluralize';
import {
  ActionHistoryFilters,
  ActionHistoryFilterState,
  emptyActionHistoryFilterState,
} from './action-history-filters';
import { ActionHistoryTable } from './action-history-table';
import {
  useActionHistory,
  useActionHistoryOptions,
} from './use-action-history';

type Props = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

export const ActionHistoryPanel: FC<Props> = ({ isOpen, onClose }) => {
  const [filter, setFilter] = useState<ActionHistoryFilterState>(
    emptyActionHistoryFilterState
  );

  const { entries, loading, error, retry, hasMore, isFetchingMore, fetchMore } = useActionHistory(isOpen, {
    actionType: filter.actionType,
    userId: filter.userId,
    from: filter.from,
    to: filter.to,
  });

  const { users, actionTypes } = useActionHistoryOptions(isOpen);

  // Reset filters each time the drawer is opened.
  useEffect(() => {
    if (isOpen) {
      setFilter(emptyActionHistoryFilterState);
    }
  }, [isOpen]);

  const renderContent = () => {
    if (loading) {
      return (
        <Center py="20">
          <Spinner size="xl" data-testid="action-history-loading" />
        </Center>
      );
    }

    if (entries.length === 0) {
      return (
        <EmptyState
          title="No records match the selected filters"
          h="40vh"
        />
      );
    }

    return (
      <Stack spacing="8">
        <ActionHistoryTable entries={entries} />
        {hasMore && (
          <Button
            isLoading={isFetchingMore}
            data-testid="action-history-fetch-more-btn"
            onClick={fetchMore}
          >
            Show more
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <Drawer size="xl" placement="right" isOpen={isOpen} onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton zIndex={1} data-testid="close-action-history-btn" />
        <DrawerHeader>
          <Heading size="lg">Action History</Heading>
          <Text fontSize="sm" color="gray.500" fontWeight="normal" mt={1}>
            All user management operations across the platform
          </Text>
        </DrawerHeader>
        <DrawerBody
          as={Flex}
          flexDirection="column"
          gridGap="4"
          overflow="hidden"
        >
          {error && (
            <ErrorState
              title="An error occurred while loading Action History"
              subtitle="Please reach out to the technical support team for further assistance."
              h="50vh"
              // eslint-disable-next-line react/jsx-no-bind
              onClick={retry}
            />
          )}

          {!error && (
            <>
              <ActionHistoryFilters
                users={users}
                actionTypes={actionTypes}
                filter={filter}
                onChange={setFilter}
              />

              <Text
                fontSize="sm"
                color="gray.500"
                alignSelf="flex-end"
                data-testid="action-history-count"
              >
                {entries.length} {pluralize('record', entries.length)}
              </Text>

              <Box flex={1} minH={0} overflowY="auto">
                {renderContent()}
              </Box>
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
